import { footerColumns } from '@/constants/footerMenu';
import { links, type NavLink } from '@/constants/navbarMenu';
import { getBlogPostPaths } from '@/lib/routing/blogPosts';

/** English labels for top-level nav entries that only expose `i18nKey` (matches `messages/en.json` `nav.root`). */
const NAV_ROOT_LABEL_EN: Record<string, string> = {
  'root.services': 'Services',
  'root.technologies': 'Technologies',
  'root.aiAgents': 'AI & Agents',
  'root.industries': 'Industries',
  'root.hire': 'Hire',
  'root.portfolio': 'Portfolio',
  'root.about': 'About',
  'root.contact': 'Contact Us',
};

function humanizeSegment(segment: string): string {
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function labelForNavLink(link: NavLink): string {
  if (link.label?.trim()) return link.label.trim();
  if (link.i18nKey && NAV_ROOT_LABEL_EN[link.i18nKey]) return NAV_ROOT_LABEL_EN[link.i18nKey];
  if (link.href) {
    const tail = link.href.replace(/^\//, '').split('/').pop() ?? '';
    return humanizeSegment(tail);
  }
  return 'Page';
}

function walkNav(linksTree: readonly NavLink[], acc: Map<string, string>): void {
  for (const link of linksTree) {
    if (link.href) {
      const path = link.href.replace(/^\//, '').replace(/\/$/, '');
      if (path) acc.set(path, labelForNavLink(link));
    }
    if (link.children?.length) walkNav(link.children, acc);
  }
}

/** Standalone marketing routes (not always in the deep nav tree). */
const EXTRA_MARKETING_PATHS: Record<string, string> = {
  about: 'About Us',
  company: 'Company',
  consultation: 'Consultation',
  contact: 'Contact Us',
  help: 'Help Center',
  careers: 'Careers',
  resources: 'Resources',
  blog: 'Blog',
  privacy: 'Privacy Policy',
  pricing: 'Pricing',
  sitemap: 'Site Map',
};

function buildPathTitleMap(): Map<string, string> {
  const acc = new Map<string, string>();
  walkNav(links, acc);
  for (const col of footerColumns) {
    for (const item of col.links) {
      const path = item.href.replace(/^\//, '').replace(/\/$/, '');
      if (path) acc.set(path, item.label ?? humanizeSegment(path.split('/').pop() ?? path));
    }
  }
  for (const [path, label] of Object.entries(EXTRA_MARKETING_PATHS)) {
    if (!acc.has(path)) acc.set(path, label);
  }
  for (const { path, title } of getBlogPostPaths()) {
    if (!acc.has(path)) acc.set(path, title);
  }
  return acc;
}

const PATH_TITLE_MAP: ReadonlyMap<string, string> = buildPathTitleMap();

export function getMarketingPathTitleMap(): ReadonlyMap<string, string> {
  return PATH_TITLE_MAP;
}

export function getMarketingPageBySlug(slug: readonly string[]): { path: string; title: string } | null {
  if (!slug.length) return null;
  const path = slug.map((s) => decodeURIComponent(s)).join('/');
  const title = PATH_TITLE_MAP.get(path);
  if (!title) return null;
  return { path, title };
}

export type BreadcrumbCrumb = { name: string; item: string };

/** Schema.org BreadcrumbList `itemListElement` — one entry per path prefix. */
export function getMarketingBreadcrumbList(
  locale: string,
  path: string,
  map: ReadonlyMap<string, string> = PATH_TITLE_MAP
): BreadcrumbCrumb[] {
  const segments = path.split('/').filter(Boolean);
  const out: BreadcrumbCrumb[] = [];
  for (let i = 0; i < segments.length; i++) {
    const prefix = segments.slice(0, i + 1).join('/');
    const name = map.get(prefix) ?? humanizeSegment(segments[i] ?? '');
    const item = `/${locale}/${prefix}`;
    out.push({ name, item });
  }
  return out;
}

export function getAllMarketingSlugParams(): { slug: string[] }[] {
  return [...PATH_TITLE_MAP.keys()].map((path) => ({ slug: path.split('/').filter(Boolean) }));
}
